import { Button } from 'components/ui/button';
import { buttonStyle } from 'components/buttons/buttonStyle';
import {
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { useCallback, useContext } from 'react';

import { useDeleteInvoiceMutation, useUpdateInvoiceStatusMutation } from 'store/api/invoices';
import { InvoiceStatusType, InvoiceTransactionActionType, InvoiceType } from 'types/invoices';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { ModalFooterSimple } from '../ModalFooterSimple';
import { ModalHeader } from '../ModalHeader';
import { InAlertDialogContext } from '../ModalProvider';

export const DELETE_EMPTY_INVOICE_PERMISSION = 'DELETE.INVOICES.DELETE_EMPTY_INVOICES.ALL';
export const POSTPONE_EMPTY_INVOICE_PERMISSION = 'UPDATE.INVOICES.POSTPONE_EMPTY_INVOICES.ALL';

export const canInvoiceTransitionToPostponed = (
    invoiceStatusTypeCode: string | null | undefined,
    invoiceTypeCode: string | null | undefined
): boolean =>
    invoiceStatusTypeCode === InvoiceStatusType.REVIEWED &&
    invoiceTypeCode !== InvoiceType.CREDIT &&
    invoiceTypeCode !== InvoiceType.CORRECTION;

export const isEmptyInvoicePromptEligibleType = (invoiceTypeCode: string | null | undefined): boolean =>
    invoiceTypeCode === InvoiceType.DEBIT || invoiceTypeCode === InvoiceType.PREPAYMENT;

interface Props {
    invoiceId: number;
    invoiceStatusTypeCode: string;
    invoiceTypeCode: string;
    onClose: () => void;
    onDeleted?: () => void;
    onPostponed?: () => void;
    /** Asks the modal stack for the kit alert dialog, which the new design draws this decision in. */
    alert?: boolean;
    /** The new design's message names the invoice. */
    invoiceNumber?: string | null;
}

const EmptyInvoicePromptModal = (props: Props) => {
    const { invoiceId, invoiceStatusTypeCode, invoiceTypeCode, onClose, onDeleted, onPostponed, invoiceNumber } = props;

    const canDelete = useHasPermissions([DELETE_EMPTY_INVOICE_PERMISSION]);
    const hasPostponePermission = useHasPermissions([POSTPONE_EMPTY_INVOICE_PERMISSION]);
    const canPostpone =
        hasPostponePermission && canInvoiceTransitionToPostponed(invoiceStatusTypeCode, invoiceTypeCode);

    const [deleteInvoice, { isLoading: isDeleting }] = useDeleteInvoiceMutation();
    const [updateInvoiceStatus, { isLoading: isPostponing }] = useUpdateInvoiceStatusMutation();
    const inAlertDialog = useContext(InAlertDialogContext);

    const handleDelete = useCallback(async () => {
        await deleteInvoice({ invoiceId })
            .unwrap()
            .then(() => {
                onClose();
                onDeleted?.();
            })
            .catch(() => errorAlert(t('invoices.empty-prompt.delete-error')));
    }, [deleteInvoice, invoiceId, onClose, onDeleted]);

    const handlePostpone = useCallback(async () => {
        await updateInvoiceStatus({
            id: invoiceId,
            invoiceStatusTypeCode: InvoiceStatusType.POSTPONED,
            transitionActionTypeCode: InvoiceTransactionActionType.POSTPONED,
            transitionComment: 'Empty invoice',
        })
            .unwrap()
            .then(() => {
                onClose();
                onPostponed?.();
            })
            .catch(() => errorAlert(t('invoices.empty-prompt.postpone-error')));
    }, [updateInvoiceStatus, invoiceId, onClose, onPostponed]);

    const titleKey =
        canDelete && canPostpone
            ? 'invoices.empty-prompt.title'
            : canDelete
              ? 'invoices.empty-prompt.title-delete-only'
              : 'invoices.empty-prompt.title-postpone-only';
    const messageKey =
        canDelete && canPostpone
            ? 'invoices.empty-prompt.message'
            : canDelete
              ? 'invoices.empty-prompt.message-delete-only'
              : 'invoices.empty-prompt.message-postpone-only';

    if (inAlertDialog) {
        return (
            <>
                <AlertDialogHeader>
                    <AlertDialogTitle>{t(titleKey)}</AlertDialogTitle>
                    <AlertDialogDescription>
                        {t(messageKey, { invoice: invoiceNumber || 'none' })}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        {t('invoices.empty-prompt.keep')}
                    </Button>
                    {canPostpone && (
                        <Button
                            variant={canDelete ? 'outline' : 'default'}
                            onClick={() => {
                                void handlePostpone();
                            }}
                            disabled={isPostponing || isDeleting}
                        >
                            {t('invoices.empty-prompt.postpone')}
                        </Button>
                    )}
                    {canDelete && (
                        <Button
                            variant="destructive"
                            onClick={() => {
                                void handleDelete();
                            }}
                            disabled={isPostponing || isDeleting}
                        >
                            {t('invoices.confirm.delete-invoice')}
                        </Button>
                    )}
                </AlertDialogFooter>
            </>
        );
    }

    const buttons = [
        <Button {...buttonStyle({ variant: 'outlined', color: 'primary' })} key="cancel" onClick={onClose}>
            {t('invoices.empty-prompt.keep')}
        </Button>,
    ];

    if (canPostpone) {
        buttons.push(
            <Button
                {...buttonStyle({ variant: 'outlined', color: 'secondary' })}
                key="postpone"
                onClick={() => {
                    void handlePostpone();
                }}
                disabled={isPostponing || isDeleting}
            >
                {t('invoices.empty-prompt.postpone')}
            </Button>
        );
    }

    if (canDelete) {
        buttons.push(
            <Button
                {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                key="delete"
                onClick={() => {
                    void handleDelete();
                }}
                disabled={isPostponing || isDeleting}
            >
                {t('invoices.empty-prompt.delete')}
            </Button>
        );
    }

    return (
        <>
            <ModalHeader title={t(titleKey)} onCancel={onClose} />
            <div className="h-full overflow-y-auto p-6 max-sm:px-4">
                <div className="grid gap-4">
                    <p className="text-body1">{t(messageKey)}</p>
                </div>
            </div>
            <ModalFooterSimple buttons={buttons} />
        </>
    );
};

export default EmptyInvoicePromptModal;
